import { Injectable, inject } from '@angular/core'

import { Actions, ofType } from '@ngrx/effects'
import { Store } from '@ngrx/store'
import * as Sentry from '@sentry/angular'
import { produce } from 'immer'
import { v4 as uuidv4 } from 'uuid'

import {
  EMPTY,
  Observable,
  catchError,
  defaultIfEmpty,
  map,
  of,
  shareReplay,
  switchMap,
  take,
  takeUntil,
  tap,
  throwError,
} from 'rxjs'

import { ContextDocumentContent } from '@flowx/designer/modules/ai/agents/documents/api/documents.api'
import { ConversationApiService } from '@flowx/designer/modules/ai/common/api/conversation-api.service'
import {
  ActionAgent,
  ActionTarget,
  AddThreadMessageRequest,
  AddThreadMessageResponse,
  AddThreadRequest,
  AddThreadResponse,
  ContextData,
  ContextItem,
  ConversationDocumentAttentionEntryInput,
  ConversationDocumentReferenceInput,
  ConversationThread,
  ConversationThreadContextDataItemType,
  ConversationThreadExecutionPlanActionType,
  CreateConversationRequest,
  CreateConversationResponse,
  Message,
} from '@flowx/designer/modules/ai/common/api/conversation-api.types'
import { AiConversationContextBuilderService } from '@flowx/designer/modules/ai/conversation/ai-conversation-context-builder.service'
import { AiConversationStreamService } from '@flowx/designer/modules/ai/conversation/ai-conversation-stream.service'
import {
  AgentAction,
  AgentName,
} from '@flowx/designer/modules/ai/conversation/ai-conversation.commons'
import {
  ChatMessage,
  ConversationDocument,
} from '@flowx/designer/modules/ai/conversation/ai-conversation.dictionary'
import {
  ContextDataKeys,
  MessageType,
} from '@flowx/designer/modules/ai/conversation/ai-conversation.model'
import { ConversationActions } from '@flowx/designer/modules/ai/conversation/conversation.actions'
import { ConversationSelectors } from '@flowx/designer/modules/ai/conversation/conversation.selectors'
import {
  buildConversationThreadSystemMessageInput,
  buildSystemMessageFromConversationThreadMessage,
  getFileContextKey,
  shouldAutoStartExecutionPlan,
} from '@flowx/designer/modules/ai/conversation/conversation.utils'
import { AiConversationServiceActions } from '@flowx/designer/modules/ai/store/ai.actions'
import { ToastMessageService } from '@flowx/designer/shared/services/toast-message.service'
import {
  selectAppId,
  selectProcessResourceId,
} from '@flowx/designer/shared/store/selectors/router.selectors'

import { AiConversationFilesService } from './ai-conversation-files.service'
import { ConversationErrorActions, ConversationErrorContext } from './conversation-error.actions'

@Injectable({
  providedIn: 'root',
})
export class AiConversationService {
  #toastMessageService = inject(ToastMessageService)
  #store = inject(Store)
  #actions$ = inject(Actions)
  #conversationApiService = inject(ConversationApiService)
  #aiConversationStreamService = inject(AiConversationStreamService)
  #aiConversationContextBuilderService = inject(AiConversationContextBuilderService)
  #aiConversationFilesService = inject(AiConversationFilesService)

  addSimpleThread(payload: {
    message: string
    contextData: ContextData
    agent?: ActionAgent
    target?: ActionTarget
  }): Observable<AddThreadResponse> {
    const conversationId = this.#store.selectSignal(ConversationSelectors.activeConversationId)()
    return this.#conversationApiService
      .addThread(conversationId, {
        message: payload.message,
        documents: [],
        contextData: payload.contextData,
        hint: {
          autoStart: false,
          actions: [
            {
              actionType: ConversationThreadExecutionPlanActionType.Automatic,
              agent: payload.agent ?? {
                agent: AgentName.ConnectedGraph,
                action: AgentAction.NoopStore,
              },
              localId: uuidv4(),
              context: { items: [] },
              description: '',
              name: '',
              parallel: false,
              target: payload.target ?? {
                name: null,
                targetType: null,
              },
            },
          ],
        },
      })
      .pipe(
        tap((response) => {
          this.#saveThreadToNgrxStore(response)
          this.#aiConversationStreamService
            .listenForMessageUpdates({
              conversationId,
              threadId: response.thread.id,
              messageId: response.message.id,
            })
            .subscribe()
        }),
        catchError((error) => {
          console.error('[Conversation Error] Failed to add simple thread:', error)
          Sentry.captureException(error, { tags: { context: 'ai-conversation.addSimpleThread' } })
          this.#store.dispatch(
            ConversationErrorActions.simpleThreadFailed({
              error,
              agentName: payload.agent?.agent || AgentName.ConnectedGraph,
              conversationId,
            })
          )
          return EMPTY
        })
      )
  }

  implementHintedAction(hintedAction: {
    message?: string
    context: ContextData
    target: ActionTarget
    userPromptRequired: boolean
    agent: ActionAgent | null
    file?: { id: string; type: string; name: string }
    hintContextItems?: ContextItem[]
  }): Observable<any> {
    const activeAgentThreadExists = this.#store.selectSignal(
      ConversationSelectors.activeAgentThreadExists
    )()
    const activeConversationExists = this.#store.selectSignal(
      ConversationSelectors.activeConversationExists
    )()
    const activeThreadHasPendingOutcome = this.#store.selectSignal(
      ConversationSelectors.activeAgentThreadHasPendingOutcome
    )()

    if (activeConversationExists) {
      if (activeAgentThreadExists) {
        if (activeThreadHasPendingOutcome) {
          throw new Error(`Can't start a new thread when we have an active thread with outcome.`)
        } else {
          // Callers subscribe from components this flow itself destroys, so the discard
          // and the replacement thread run detached from the caller's subscription.
          const detachedThreadReplacement = this.discardThread().pipe(
            catchError((error) => {
              console.error('[Conversation Error] Failed to discard the superseded thread:', error)
              return of(null)
            }),
            defaultIfEmpty(null),
            switchMap(() => this.#addThread(hintedAction)),
            shareReplay({ bufferSize: 1, refCount: false })
          )
          detachedThreadReplacement.subscribe({ error: () => undefined })

          return detachedThreadReplacement
        }
      } else {
        return this.#addThread(hintedAction)
      }
    } else {
      return this.#addConversation(hintedAction)
    }
  }

  closeConversation(conversationId: string): Observable<void> {
    return this.#conversationApiService.closeConversation(conversationId).pipe(
      tap(() => {
        this.#aiConversationStreamService.cleanup()
      }),
      catchError((error) => {
        this.#aiConversationStreamService.cleanup()
        console.error('[Conversation Error] Failed to close conversation')
        Sentry.captureException(error, { tags: { context: 'ai-conversation.closeConversation' } })

        return throwError(() => error)
      })
    )
  }

  validateMessageAttachments(documents?: ConversationDocumentReferenceInput[]): boolean {
    const hasAttachments =
      !!this.#store.selectSignal(ConversationSelectors.uploadedFiles)()?.length ||
      !!documents?.length
    if (hasAttachments && !this.#store.selectSignal(ConversationSelectors.attachmentsAllowed)()) {
      this.#toastMessageService.showErrorMessage(
        'UI flow generation and editing support text prompts only. Remove the attachment to continue.'
      )
      return false
    }
    return true
  }

  submitMessage(
    message: ChatMessage,
    documents?: ConversationDocumentReferenceInput[]
  ): Observable<any> {
    if (!this.validateMessageAttachments(documents)) {
      return EMPTY
    }

    const activeAgentThreadExists = this.#store.selectSignal(
      ConversationSelectors.activeAgentThreadExists
    )()
    const activeConversationExists = this.#store.selectSignal(
      ConversationSelectors.activeConversationExists
    )()
    const uploadedFiles = this.#store.selectSignal(ConversationSelectors.uploadedFiles)()

    // Check if there are files to upload (only support one file for now)
    const fileToUpload = uploadedFiles?.length > 0 ? uploadedFiles[0] : null
    // If there's a file to upload, upload it first and then send the message with the file ID
    if (fileToUpload && 'file' in fileToUpload) {
      const conversationId = this.#store.selectSignal(ConversationSelectors.activeConversationId)()

      return this.#aiConversationFilesService.uploadFile(fileToUpload.file).pipe(
        switchMap((fileId) => {
          // The active action may have changed while the upload was in flight.
          if (!this.validateMessageAttachments(documents)) {
            return EMPTY
          }
          // Clear uploaded files after successful upload
          this.#store.dispatch(ConversationActions.clearUploadedFiles())

          // Add the file metadata to the message context
          const messageWithFileContext = {
            ...message,
            file: {
              id: fileId,
              type: fileToUpload.type,
              name: fileToUpload.name,
            },
          }

          if (activeAgentThreadExists) {
            return this.#addThreadMessage(messageWithFileContext, documents)
          } else if (activeConversationExists) {
            return this.#addDefaultThreadMessage(messageWithFileContext)
          } else {
            return this.#startConversationWithIntentUnderstanding(messageWithFileContext, documents)
          }
        }),
        catchError((error) => {
          console.error('[Conversation Error] File upload failed:', error)
          Sentry.captureException(error, {
            tags: { context: 'ai-conversation.submitMessage.fileUpload' },
          })
          this.#store.dispatch(
            ConversationErrorActions.fileUploadFailed({
              error,
              conversationId,
            })
          )
          return EMPTY
        })
      )
    }

    // If there's an active agent thread, always add a message to it
    // (regardless of page context — e.g. custom component pages have no process)
    if (activeAgentThreadExists) {
      return this.#addThreadMessage(message, documents)
    }

    // Outside a process page, route messages to the assistant
    const isInProcess = this.#store.selectSignal(ConversationSelectors.selectedProcessId)() !== null
    if (!isInProcess) {
      return this.#submitAssistantMessage(message)
    }

    // No file to upload, proceed normally
    if (activeConversationExists) {
      return this.#addDefaultThreadMessage(message)
    } else {
      return this.#startConversationWithIntentUnderstanding(message, documents)
    }
  }

  discardOutcomeThread(addDiscardMessage: boolean = true): Observable<any> {
    const activeAgentThreadExists = this.#store.selectSignal(
      ConversationSelectors.activeAgentThreadExists
    )()

    const conversationId = this.#store.selectSignal(ConversationSelectors.activeConversationId)()
    const threadId = this.#store.selectSignal(ConversationSelectors.activeAgentThreadId)()
    const currentOutcome = this.#store.selectSignal(ConversationSelectors.currentOutcomeBeingKept)()
    // Get the message with the agent action whose outcome would be discarded
    const messageWithOutcome = this.#store.selectSignal(
      ConversationSelectors.latestAgentMessageWithAgentAction
    )()

    if (activeAgentThreadExists) {
      return (
        addDiscardMessage
          ? this.addStoreMessage({
              prompt: `Discarding ${currentOutcome ?? 'thread outcome'}.`,
              type: MessageType.UserMsg,
            })
          : of(null)
      ).pipe(
        switchMap(() =>
          this.#conversationApiService
            .discardThread(conversationId, threadId, {
              messageId: messageWithOutcome?.id,
              shouldFinalize: true,
            })
            .pipe(
              tap(() => {
                this.#store.dispatch(
                  // TODO(ai-conversation): Add reducer for this action, where
                  // we will mark the thread status as finished.
                  ConversationActions.discardThreadApiResponse({ conversationId, threadId })
                )
                this.#store.dispatch(ConversationActions.discardOutcomeClick())
              })
            )
        )
      )
    } else {
      throw new Error('Expected to have a thread when trying to discard thread outcome.')
    }
  }

  discardThread(
    addDiscardMessage: boolean = true,
    message: string = 'Action canceled. I am waiting on further instructions'
  ): Observable<any> {
    const activeNonDefaultThreadId = this.#store.selectSignal(
      ConversationSelectors.activeNonDefaultThreadId
    )()
    const conversationId = this.#store.selectSignal(ConversationSelectors.activeConversationId)()
    const threadId = activeNonDefaultThreadId
    const lastMessage = this.#store.selectSignal(
      ConversationSelectors.lastMessageFromActiveNonDefaultThread
    )()

    if (activeNonDefaultThreadId) {
      return (
        addDiscardMessage
          ? this.addStoreMessage({ prompt: message, type: MessageType.SystemMsg })
          : of(null)
      ).pipe(
        switchMap(() =>
          this.#conversationApiService
            .discardThread(conversationId, threadId, {
              messageId: lastMessage?.id,
              shouldFinalize: true,
            })
            .pipe(
              tap(() => {
                this.#store.dispatch(
                  ConversationActions.discardThreadApiResponse({ conversationId, threadId })
                )
              }),
              catchError((error) => {
                console.error('[Conversation Error] Failed to discard thread')
                Sentry.captureException(error, {
                  tags: { context: 'ai-conversation.discardThread' },
                })
                return EMPTY
              })
            )
        )
      )
    } else {
      throw new Error('Expected to have an active agent thread to discard.')
    }
  }

  addStoreMessage(
    message: ChatMessage,
    contextData?: ContextItem[],
    target?: ActionTarget
  ): Observable<AddThreadMessageResponse> {
    const conversationId = this.#store.selectSignal(ConversationSelectors.activeConversationId)()
    const threadId = this.#store.selectSignal(ConversationSelectors.activeNonDefaultThreadId)()

    const messageInput: AddThreadMessageRequest = {
      message: message.prompt,
      documents: [],
      contextData: {
        items: [
          ...(contextData ?? []),
          {
            key: ContextDataKeys.UIMessageType,
            itemType: ConversationThreadContextDataItemType.GenericString,
            valueGenericString: message.type,
          },
        ],
      },
      hint: {
        autoStart: false,
        actions: [
          {
            actionType: ConversationThreadExecutionPlanActionType.Automatic,
            agent: {
              action: AgentAction.NoopStore,
              agent: AgentName.ConnectedGraph,
            },
            localId: uuidv4(),
            context: { items: [] },
            description: '',
            name: '',
            parallel: false,
            target: target ?? {
              name: null,
              targetType: null,
            },
          },
        ],
      },
    }

    return this.#conversationApiService
      .addThreadMessage(conversationId, threadId, messageInput)
      .pipe(
        tap((response) => {
          this.#handleThreadMessageResponse(response, conversationId, threadId, true)
        })
      )
  }

  startConversationWithDocument(
    document: ConversationDocument,
    resource: ContextDocumentContent = null,
    attention: ConversationDocumentAttentionEntryInput[] = []
  ): Observable<any> {
    return this.#startConversationWithIntentUnderstanding(
      {
        prompt: resource
          ? `Use ${resource.name} from ${document.filename}`
          : `Use ${document.filename}`,
        type: MessageType.UserMsg,
      },
      [
        {
          docID: document.diDocumentId,
          attention: {
            entries: attention ? attention : [],
          },
        },
      ]
    )
  }

  fetchConversationThreadById(params: {
    conversationId: string
    threadId: string
  }): Observable<ConversationThread | null> {
    return this.#conversationApiService
      .fetchConversation(params.conversationId)
      .pipe(
        map(
          (conversation) =>
            conversation?.threads?.find((thread) => thread.id === params.threadId) ?? null
        )
      )
  }

  fetchMessageById(params: {
    conversationId: string
    threadId: string
    messageId: string
  }): Observable<Message | null> {
    return this.#conversationApiService.fetchConversation(params.conversationId).pipe(
      map((conversation) => {
        const thread = conversation?.threads?.find((t) => t.id === params.threadId)
        return thread?.messages?.find((m) => m.id === params.messageId) ?? null
      })
    )
  }

  storeErrorMessage(errorMessage?: string): Observable<any> {
    const conversationId = this.#store.selectSignal(ConversationSelectors.activeConversationId)()
    const activeThread = this.#store.selectSignal(ConversationSelectors.activeNonDefaultThreadId)()

    if (!conversationId) {
      return EMPTY
    }

    if (activeThread) {
      return this.addStoreMessage(
        {
          prompt:
            errorMessage || 'An error occurred while processing your request. Please try again.',
          type: MessageType.ErrorMsg,
        },
        [],
        null
      )
    } else {
      return this.#addThread({
        message:
          errorMessage || 'An error occurred while processing your request. Please try again.',
        context: {
          items: [
            {
              key: ContextDataKeys.UIMessageType,
              itemType: ConversationThreadContextDataItemType.GenericString,
              valueGenericString: MessageType.ErrorMsg,
            },
          ],
        },
        target: null,
        userPromptRequired: false,
        agent: {
          action: AgentAction.NoopStore,
          agent: AgentName.ConnectedGraph,
        },
      })
    }
  }

  #addThreadMessage(
    message: ChatMessage & { file?: { id: string; type: string; name: string } },
    documents?: ConversationDocumentReferenceInput[]
  ): Observable<any> {
    const { conversationId, threadId } = this.#getActiveConversationAndThread()
    const threadData = this.#validateThreadData()
    let messageInput = this.#aiConversationContextBuilderService.buildThreadMessageInput(
      message,
      threadData,
      documents
    )

    // Add file context to hint actions if file is provided
    if (message.file) {
      // Get the active agent to determine the correct context key
      const { firstMessage } = threadData
      const agent = this.#aiConversationContextBuilderService.extractAgentFromMessage(firstMessage)

      // Determine the correct key based on the agent
      const fileContextKey = getFileContextKey(agent.agent as AgentName)

      // Determine the action based on agent and file type
      let agentAction = agent.action
      if (agent.agent === AgentName.AiAnalyst) {
        // Check if file is BPMN
        const isBpmnFile =
          message.file.type === 'application/bpmn+xml' || message.file.name.endsWith('.bpmn')
        agentAction = isBpmnFile
          ? AgentAction.CreateProcessFromBpmn
          : AgentAction.CreateProcessFromImage
      } else if (agent.agent === AgentName.AiDesigner) {
        agentAction = AgentAction.CreateUiFromImage
      }

      messageInput = produce(messageInput, (draft) => {
        // Add file context to the action, not to message context
        if (draft.hint && draft.hint.actions && draft.hint.actions.length > 0) {
          // Update the agent action if we have a file
          if (agent.agent === AgentName.AiAnalyst || agent.agent === AgentName.AiDesigner) {
            draft.hint.actions[0].agent.action = agentAction
          }
          // Add file to action context
          draft.hint.actions[0].context.items.push({
            key: fileContextKey,
            itemType: ConversationThreadContextDataItemType.BinaryObject,
            valueBinaryObject: {
              tempFileId: message.file.id,
              fileName: message.file.name,
              mimeType: message.file.type,
            },
          })
        }
      })
    }

    return this.#conversationApiService
      .addThreadMessage(conversationId, threadId, messageInput)
      .pipe(
        tap((response) => {
          this.#handleThreadMessageResponse(response, conversationId, threadId, true)
        }),
        catchError((error) => {
          console.error('[Conversation Error] Failed to add agent thread message:', error)
          Sentry.captureException(error, { tags: { context: 'ai-conversation.addThreadMessage' } })
          this.#store.dispatch(
            ConversationErrorActions.agentMessageFailed({
              error,
              threadId,
              conversationId,
            })
          )
          return EMPTY
        })
      )
  }

  #submitAssistantMessage(message: ChatMessage): Observable<any> {
    return this.implementHintedAction({
      message: message.prompt,
      context: { items: [] },
      target: {
        name: null,
        targetType: null,
      },
      userPromptRequired: false,
      agent: {
        agent: AgentName.AiAssistant,
        action: AgentAction.AskAssistant,
      },
    })
  }

  #addDefaultThreadMessage(
    message: ChatMessage & { file?: { id: string; type: string; name: string } }
  ): Observable<any> {
    const conversationId = this.#store.selectSignal(ConversationSelectors.activeConversationId)()
    const defaultThreadId = this.#store.selectSignal(ConversationSelectors.defaultThreadId)()

    if (!conversationId) {
      throw new Error('No active conversation found')
    }

    if (!defaultThreadId) {
      throw new Error('No default thread found in conversation')
    }

    this.#store.dispatch(AiConversationServiceActions.conversationCreating())
    this.#store.dispatch(ConversationActions.addDefaultThreadMessageStarted())

    this.#store.dispatch(
      ConversationActions.updatePendingMessage({
        message: {
          message: message.prompt,
          type: message.type,
        },
      })
    )

    return this.#aiConversationContextBuilderService.buildIntentUnderstandingMessageContext().pipe(
      switchMap((contextData) => {
        // Add the message type context item to the context
        const contextWithMessageType: ContextData = {
          items: this.#aiConversationContextBuilderService.deduplicateContextItems([
            ...contextData.items,
            this.#aiConversationContextBuilderService.buildMessageTypeContextItem(message.type),
          ]),
        }

        const messageInput: AddThreadMessageRequest = {
          message: message.prompt,
          documents: [],
          // Add hint with file context if file is provided
          ...(message.file
            ? {
                hint: this.#aiConversationContextBuilderService.buildIntentUnderstandingHint(
                  message.file
                ),
              }
            : {}),
          contextData: contextWithMessageType,
        }

        return this.#conversationApiService
          .addThreadMessage(conversationId, defaultThreadId, messageInput)
          .pipe(
            takeUntil(
              this.#actions$.pipe(
                ofType(
                  ConversationActions.defaultThreadMessageCancelled,
                  ConversationActions.discardThreadClick
                ),
                take(1),
                tap(() => {
                  this.#store.dispatch(ConversationActions.updatePendingMessage({ message: null }))
                })
              )
            ),
            tap((response) => {
              this.#store.dispatch(AiConversationServiceActions.conversationCreated())
              this.#handleThreadMessageResponse(response, conversationId, defaultThreadId, false)
              this.#store.dispatch(ConversationActions.updatePendingMessage({ message: null }))
              this.#store.dispatch(ConversationActions.addDefaultThreadMessageSuccess())
            }),
            switchMap((response) => {
              const executionPlan = response.executionPlan

              if (shouldAutoStartExecutionPlan(executionPlan)) {
                this.#store.dispatch(
                  ConversationActions.autoStartAssistantAction({
                    action: executionPlan.actions[0],
                    skipUserConfirmation: true,
                  })
                )
                return EMPTY
              }

              return this.#handleExecutionPlanResponse(conversationId, defaultThreadId, response)
            }),
            catchError((error) => {
              console.error('[Conversation Error] Failed to add default thread message:', error)
              Sentry.captureException(error, {
                tags: { context: 'ai-conversation.addDefaultThreadMessage' },
              })
              this.#store.dispatch(ConversationActions.addDefaultThreadMessageError())
              this.#store.dispatch(
                ConversationErrorActions.intentUnderstandingFailed({
                  error,
                  context: ConversationErrorContext.DefaultThreadMessage,
                  conversationId,
                  threadId: defaultThreadId,
                })
              )
              return EMPTY
            })
          )
      })
    )
  }

  #handleExecutionPlanResponse(
    conversationId: string,
    defaultThreadId: string,
    responseMessage: Message
  ): Observable<any> {
    const hasMultipleExecutionPlans = this.#store.selectSignal(
      ConversationSelectors.hasMultiplePendingExecutionPlansOnDefaultThread
    )()
    const enhanceExecutionPlanDiff = this.#store.selectSignal(
      ConversationSelectors.enhanceExecutionPlanDiff
    )()

    // Enhance with only an assistant action added → auto-run it
    if (hasMultipleExecutionPlans && !enhanceExecutionPlanDiff) {
      const currentPlan = this.#store.selectSignal(ConversationSelectors.currentExecutionPlan)()
      const currentActionIds = new Set(currentPlan?.actions?.map((a) => a.localId) ?? [])
      const newAssistantAction = (responseMessage.executionPlan?.actions ?? []).find(
        (a) => !currentActionIds.has(a.localId) && a.agent?.agent === AgentName.AiAssistant
      )

      if (newAssistantAction) {
        this.#store.dispatch(
          ConversationActions.autoStartAssistantAction({
            action: newAssistantAction,
            skipUserConfirmation: true,
          })
        )
        return EMPTY
      }
    }

    if (!hasMultipleExecutionPlans || (hasMultipleExecutionPlans && !enhanceExecutionPlanDiff)) {
      const messageInput = buildConversationThreadSystemMessageInput(
        buildSystemMessageFromConversationThreadMessage(
          responseMessage,
          hasMultipleExecutionPlans && !enhanceExecutionPlanDiff
        )
      )

      return this.#conversationApiService
        .addThreadMessage(conversationId, defaultThreadId, messageInput)
        .pipe(
          tap((response) => {
            this.#handleThreadMessageResponse(response, conversationId, defaultThreadId, true)
          }),
          catchError((systemMessageError) => {
            console.error('[Conversation Error] Failed to add system message:', systemMessageError)
            Sentry.captureException(systemMessageError, {
              tags: { context: 'ai-conversation.addDefaultThreadMessage.systemMessage' },
            })
            this.#store.dispatch(
              ConversationErrorActions.intentUnderstandingFailed({
                error: systemMessageError,
                context: ConversationErrorContext.SystemMessage,
                conversationId,
                threadId: defaultThreadId,
              })
            )
            return EMPTY
          })
        )
    }

    return EMPTY
  }

  #startConversationWithIntentUnderstanding(
    message: ChatMessage & { file?: { id: string; type: string; name: string } },
    documents?: ConversationDocumentReferenceInput[]
  ): Observable<any> {
    // Dispatch action to show loading state
    this.#store.dispatch(AiConversationServiceActions.conversationCreating())
    this.#store.dispatch(
      ConversationActions.updatePendingMessage({
        message: {
          message: message.prompt,
          type: message.type,
        },
      })
    )

    return this.#aiConversationContextBuilderService.buildIntentUnderstandingMessageContext().pipe(
      switchMap((contextData) => {
        const conversationInput: CreateConversationRequest = {
          applicationId: this.#store.selectSignal(selectAppId)(),
          processResourceId: this.#store.selectSignal(selectProcessResourceId)(),
          message: message.prompt,
          documents: documents ?? [],
          contextData: {
            items: this.#aiConversationContextBuilderService.deduplicateContextItems([
              ...contextData.items,
              this.#aiConversationContextBuilderService.buildMessageTypeContextItem(message.type),
            ]),
          },
          // Add hint with file context if file is provided
          ...(message.file
            ? {
                hint: this.#aiConversationContextBuilderService.buildIntentUnderstandingHint(
                  message.file
                ),
              }
            : {}),
        }

        return this.#conversationApiService.createConversation(conversationInput).pipe(
          takeUntil(
            this.#actions$.pipe(
              ofType(ConversationActions.conversationCreationCancelled),
              take(1),
              tap(() => {
                this.#store.dispatch(ConversationActions.updatePendingMessage({ message: null }))
              })
            )
          ),
          tap((response) => {
            this.#saveConversationToNgrxStore(response)
            this.#store.dispatch(ConversationActions.updatePendingMessage({ message: null }))
            this.#store.dispatch(AiConversationServiceActions.conversationCreated())
          }),
          catchError((error) => {
            console.error('[Conversation Error] Failed to create conversation:', error)
            Sentry.captureException(error, {
              tags: { context: 'ai-conversation.createConversation' },
            })
            this.#store.dispatch(ConversationActions.updatePendingMessage({ message: null }))

            const conversationId = this.#store.selectSignal(
              ConversationSelectors.activeConversationId
            )()
            if (conversationId) {
              this.#store.dispatch(ConversationActions.clearActiveConversation())
            }

            this.#store.dispatch(
              ConversationErrorActions.conversationCreationFailed({
                error,
              })
            )

            return EMPTY
          }),
          switchMap((response) => {
            const responseMessage = response?.seedMessage
            const executionPlan = responseMessage?.executionPlan

            if (shouldAutoStartExecutionPlan(executionPlan)) {
              this.#store.dispatch(
                ConversationActions.autoStartAssistantAction({
                  action: executionPlan.actions[0],
                  skipUserConfirmation: true,
                })
              )
              return EMPTY
            }

            const conversationId = this.#store.selectSignal(
              ConversationSelectors.activeConversationId
            )()
            const defaultThreadId = this.#store.selectSignal(
              ConversationSelectors.defaultThreadId
            )()
            const messageInput = buildConversationThreadSystemMessageInput(
              buildSystemMessageFromConversationThreadMessage(responseMessage)
            )

            return this.#conversationApiService
              .addThreadMessage(conversationId, defaultThreadId, messageInput)
              .pipe(
                tap((response) => {
                  this.#handleThreadMessageResponse(response, conversationId, defaultThreadId, true)
                }),
                catchError((systemMessageError) => {
                  console.error(
                    '[Conversation Error] Failed to add system message after conversation creation:',
                    systemMessageError
                  )
                  Sentry.captureException(systemMessageError, {
                    tags: { context: 'ai-conversation.createConversation.systemMessage' },
                  })
                  this.#store.dispatch(
                    ConversationErrorActions.intentUnderstandingFailed({
                      error: systemMessageError,
                      context: ConversationErrorContext.SystemMessage,
                      conversationId,
                      threadId: defaultThreadId,
                    })
                  )
                  return EMPTY
                })
              )
          })
        )
      })
    )
  }

  #getActiveConversationAndThread(): { conversationId: string; threadId: string } {
    const conversationId = this.#store.selectSignal(ConversationSelectors.activeConversationId)()
    const threadId = this.#store.selectSignal(ConversationSelectors.activeAgentThreadId)()

    if (!conversationId || !threadId) {
      throw new Error('Missing conversation or thread ID')
    }

    return { conversationId, threadId }
  }

  #validateThreadData(): {
    activeThread: ConversationThread
    latestMessage: Message | null
    firstMessage: Message
  } {
    const activeThread = this.#store.selectSignal(ConversationSelectors.activeAgentThread)()
    const latestMessage = this.#store.selectSignal(ConversationSelectors.latestMessageFromAgent)()
    const firstMessage = this.#store.selectSignal(
      ConversationSelectors.firstMessageFromActiveAgent
    )()

    if (!activeThread?.messages.length) {
      throw new Error('Expected to have a thread with messages when trying to add a new message.')
    }

    if (!firstMessage) {
      throw new Error('First message not found in thread')
    }

    return { activeThread, latestMessage, firstMessage }
  }

  /**
   * Handles the response from adding a thread message by saving it to the store and optionally subscribing to message updates.
   *
   * @param response - The mutation result containing the newly added thread message
   * @param conversationId - The ID of the conversation containing the thread
   * @param threadId - The ID of the thread containing the message
   * @param subscribeToMessage - Whether to subscribe to message updates for execution tracking.
   *
   * When `subscribeToMessage` is `true`, subscribing to the message triggers the execution on the backend to obtain
   * an outcome. This will also mark the message as FINISHED once the backend execution completes.
   *
   * Even for messages with the `conversation_graph` agent and `no_op` action, subscribing is useful because it marks
   * these messages as FINISHED. Other algorithms, especially those related to execution plans, rely on messages being
   * marked as FINISHED to function correctly.
   *
   * However, we should NOT subscribe to messages that are meant for intent understanding to generate an execution plan.
   * These messages should not be marked as FINISHED until either:
   * - All tasks in the generated execution plan have been completed, OR
   * - Another execution plan has been generated
   *
   * Additionally, for intent understanding messages, we don't obtain the outcome (execution plan) by subscribing,
   * because the execution is eager (it starts immediately when we create the message).
   */
  #handleThreadMessageResponse(
    response: AddThreadMessageResponse,
    conversationId: string,
    threadId: string,
    subscribeToMessage: boolean
  ): void {
    this.#saveThreadMessageToNgrxStore(response, threadId)
    const messageId = response?.id

    if (!messageId) {
      throw new Error('Failed to get message ID from response')
    }
    // response is the bare Message (REST), not an envelope.

    if (subscribeToMessage) {
      this.#aiConversationStreamService
        .listenForMessageUpdates({
          conversationId,
          threadId,
          messageId,
        })
        .subscribe()
    }
  }

  #addThread(executionPlanStep: {
    message?: string
    context: ContextData
    target: ActionTarget
    userPromptRequired: boolean
    agent: ActionAgent | null
    file?: { id: string; type: string; name: string }
    hintContextItems?: ContextItem[]
  }): Observable<AddThreadResponse> {
    const conversation = this.#store.selectSignal(ConversationSelectors.activeConversation)()
    const conversationInput =
      this.#aiConversationContextBuilderService.buildFirstMessage(executionPlanStep)
    const threadInput: AddThreadRequest = {
      message: conversationInput.message,
      contextData: conversationInput.contextData,
      documents: conversationInput.documents,
      hint: conversationInput.hint,
    }

    return this.#conversationApiService.addThread(conversation.id, threadInput).pipe(
      tap((response) => {
        this.#saveThreadToNgrxStore(response)
        this.#aiConversationStreamService
          .listenForMessageUpdates({
            conversationId: conversation.id,
            threadId: response.thread.id,
            messageId: response.message.id,
          })
          .subscribe()
      })
    )
  }

  #addConversation(executionPlanStep: {
    message?: string
    context: ContextData
    target: ActionTarget
    userPromptRequired: boolean
    agent: ActionAgent | null
    file?: { id: string; type: string; name: string }
    hintContextItems?: ContextItem[]
  }): Observable<unknown> {
    const conversationInputWithoutMessage =
      this.#aiConversationContextBuilderService.buildConversationInputWithoutMessage()

    return this.#conversationApiService.createConversation(conversationInputWithoutMessage).pipe(
      tap((response) => {
        this.#saveConversationToNgrxStore(response)
        this.#store.dispatch(AiConversationServiceActions.conversationCreated())
      }),
      switchMap((createResponse) => {
        // Now create the actual thread with context and message
        const conversationId = createResponse.conversation.id
        const conversationInput =
          this.#aiConversationContextBuilderService.buildFirstMessage(executionPlanStep)
        const threadInput: AddThreadRequest = {
          message: conversationInput.message,
          contextData: conversationInput.contextData,
          documents: conversationInput.documents,
          hint: conversationInput.hint,
        }

        return this.#conversationApiService.addThread(conversationId, threadInput).pipe(
          tap((threadResponse) => {
            this.#saveThreadToNgrxStore(threadResponse)
            this.#aiConversationStreamService
              .listenForMessageUpdates({
                conversationId,
                threadId: threadResponse.thread.id,
                messageId: threadResponse.message.id,
              })
              .subscribe()
          })
        )
      })
    )
  }

  #saveConversationToNgrxStore(response: CreateConversationResponse): void {
    const { conversation, seedMessage } = response

    // REST returns the seed message alongside the conversation rather than
    // embedded in the thread; merge it back into its thread so downstream
    // consumers (auto-start, plan lookup) see a non-empty default thread.
    const conversationWithSeed = seedMessage
      ? {
          ...conversation,
          threads: conversation.threads.map((thread) =>
            thread.id === seedMessage.threadId
              ? { ...thread, messages: [...(thread.messages ?? []), seedMessage] }
              : thread
          ),
        }
      : conversation

    this.#store.dispatch(
      ConversationActions.createConversationApiResponse({
        conversation: conversationWithSeed,
      })
    )
  }

  #saveThreadToNgrxStore(response: AddThreadResponse): void {
    this.#store.dispatch(
      ConversationActions.createThreadApiResponse({
        thread: {
          ...response.thread,
          messages: [response.message],
        },
      })
    )
  }

  #saveThreadMessageToNgrxStore(response: AddThreadMessageResponse, threadId: string): void {
    this.#store.dispatch(
      ConversationActions.addThreadMessageApiResponse({
        threadMessage: response,
        threadId,
      })
    )
  }
}
